import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { SelectableSettings } from '@progress/kendo-angular-grid';
import { take } from 'rxjs/operators';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { ImportPreviewRenderService } from '../../../../components/common/services/import-preview-render-service';
import { GenericPolluxResult } from '../../../../models/generic-pollux-result';
import { AppServiceHelperService } from '../../../../services/app-service-helper.service';
import { FileDownloadService } from '../../../../services/file-download-service.service';
import { EventBusService } from '../../../../services/event-bus.service';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Export wizard for ImportV2
 */
@Component({
  selector: 'export-wizard',
  templateUrl: './export-wizard.component.html',
  styleUrls: ['./export-wizard.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ExportWizardComponent extends DestroyBase implements OnInit {
  /**
   * The Oid
   */
  @Input() oid: any;

  /**
   * Project ID
   */
  @Input() projectId: number;

  /**
   * Custome exports, not defined in Dataverse by template
   */
  customExports = [
    {
      id: -1, templateName: 'Analysis Tool', createdAt: null, exportFunction: this.exportAnalysisTool
    },
    {
      id: -2, templateName: 'Barings Format', createdAt: null, exportFunction: this.exportBaringsFormat
    }
  ];

  /**
   * The available templates (published and unpublished)
   */
  public templates: any[] = [];

  /**
   * The templates shown in grid (by default only published, Business Analyst can see Unpublished too)
   */
  public gridTemplates: any[] = [];

  /**
   * The Kendo grid selectable settings
   */
  public selectableSettings: SelectableSettings = {
    checkboxOnly: false,
    mode: 'single',
    drag: false
  };

  /**
   * The ids of the selected export templates
   */
  public selectedExports: number[] = [];

  /**
   * Cloas epane event
   */
  @Output() closePane: EventEmitter<any> = new EventEmitter();

  /**
   * Should the dialog be closed?
   */
  shouldClose = false;

  /**
   * Indicates if the user has role Business Analyst
   */
  isBusinessAnalyst = false;

  /**
   * Is choosen (radio) Published (true) or Unpublished(false)
   */
  publishedExportsView = true;//default = show published

  /**
   * Constructor
   * @param iprs ImportPreviewRenderService
   */
  constructor(private eventBus: EventBusService,
    private appStorage: AppStorageService,
    public iprs: ImportPreviewRenderService,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService,
    private fileDownloadService: FileDownloadService) {
    super();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.isBusinessAnalyst = this.appStorage.userIsInRole(['Business Analyst']);
    this.selectableSettings.mode = this.isBusinessAnalyst ? 'multiple' : 'single';

    this.loadTemplates();
  }

  /**
   * Loads the templates
   */
  loadTemplates() {
    const url = `export/v2/templates`;
    this.http.get(this.appServiceHelper.getServiceEndpoint('pollux.import.api', url))
      .pipe(take(1)).subscribe({
        next: (templates: any[]) => {
          templates.forEach(template => {
            template.exportFunction = this.exportSelfServiceReport;
          });

          this.templates = templates;
          this.setupGridData();

        },
        error: (_error) => {
          this.eventBus.broadcastEvent('project.valuation.showLoader', false);
        }
      });
  }

  /**
   * Setup templates to be shown in grid
   */
  setupGridData() {
    if (this.publishedExportsView) {
      this.gridTemplates = [...this.customExports, ...this.templates.filter(t => t.published === 1)];
    } else {
      this.gridTemplates = [...this.templates.filter(t => t.published !== 1)];
    }

    this.selectedExports = [];
  }

  /**
   * Export the selected template(s)
   */
  public export() {
    const exportsToTrigger = this.templates.filter((template) => { return this.selectedExports.indexOf(template.id) >= 0; });
    if (exportsToTrigger.length > 0) {
      //we have mode: 'single', so only one can be exported at the time
      const template = exportsToTrigger[0];

      this.eventBus.broadcastEvent('project.valuation.showLoader', true);
      const promises = [template.exportFunction(self, template.id)];
      Promise.all(promises).then(() => {
        this.eventBus.broadcastEvent('project.valuation.showLoader', false);
      });
    }
    else if (this.selectedExports.length) {
      const selected = this.selectedExports[0];
      if (selected < 0) {
        const customExport = this.customExports.find((en) => en.id === selected);
        if (customExport) {
          customExport.exportFunction(this, selected);
        }
      }
    }
  }

  /**
   * Exports the Self Service Report
   * @param self this context
   * @param templateId template Id
   */
  private exportSelfServiceReport(self: ExportWizardComponent, templateId: number) {
    const payload = {
      templateId,
      valuationPortfolioId: self.oid
    };

    const url = `export/v2/export`;

    return self.fileDownloadService.initiateDownloadPost(self.appServiceHelper.getServiceEndpoint('pollux.import.api', url), payload);
  }

  /**
   * Exports the Analysis Tool
   * @param self this context
   */
  private exportAnalysisTool(self: ExportWizardComponent, _templateId = 0) {
    return this.fileDownloadService.initiateDownload(self.appServiceHelper.getServiceEndpoint('pollux.import.api', 'import/v2/export?groupId=1&referenceBoOid=' + self.oid), 'Analysis Tool Export.xlsx', true);
  }

  /**
   * Exports the Barings format
   * @param self this context
   */
  private exportBaringsFormat(self: ExportWizardComponent, _templateId = 0) {
    return this.fileDownloadService.initiateDownload(self.appServiceHelper.getServiceEndpoint('pollux.import.api', 'import/v2/export?groupId=2&referenceBoOid=' + self.oid), 'Barings Format Export.xlsx', true);
  }

  /**
   * Close was clicked
   */
  onCloseClicked() {
    this.shouldClose = true;
    setTimeout(() => {
      this.closePane.emit();
    }, 500);//let animation finish
  }

  /**
   * Published/unpublished radio clicked
   */
  showPublishedExportsChoosen(showPublished: boolean) {
    this.publishedExportsView = showPublished;
    this.setupGridData();
  }

  /**
   * Publish/unpublish button clicked
   */
  publishUnpublishClicked() {
    const ids = this.selectedExports.filter(id => id > 0);//custom exports can't be unpublished/published
    if (ids.length > 0) {


      const handleSuccess = () => {
        const newPublishedValue = this.publishedExportsView ? 0 : 1;
        for (const template of this.templates.filter(t => ids.indexOf(t.id) !== -1)) {
          template.published = newPublishedValue;
        }

        this.setupGridData();
      };

      const handleError = () => {
        alert("Failed to publish/unpublish!");
      };

      const action = this.publishedExportsView ? 'unpublish' : 'publish';
      const url = this.appServiceHelper.getServiceEndpoint('pollux.import.api', `export/v2/templates/${action}`);
      const payload = {
        templateIds: ids
      };

      this.http.post(url, payload).subscribe({
        next: (gpr: GenericPolluxResult) => {
          if (gpr.success) {
            handleSuccess();
          } else {
            handleError();
          }
        },
        error: (_error) => {
          handleError();
        }
      });
    }
  }
}
